import { useState, useEffect } from 'react';
import { staffService, attendanceService, payoutService, payoutAccountService, expenseCategoryService, expenseService, Staff, Payout, PayoutAccount, Expense } from '@/lib/storage';
import { GlassCard, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { 
  Banknote, 
  CheckCircle, 
  AlertCircle, 
  QrCode, 
  ArrowUpRight, 
  Search, 
  Calendar, 
  RefreshCw, 
  Trash2,
  CreditCard,
  Plus,
  ArrowRight,
  ArrowLeft,
  DollarSign,
  History,
  Info,
  Clock3
} from 'lucide-react';

const AdminPayoutsPage = () => {
  const [activeTab, setActiveTab] = useState<'payroll' | 'paid-transactions'>('payroll');
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [staffStats, setStaffStats] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(''); // e.g. "2026-07"
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Custom categories list
  const [categoriesList, setCategoriesList] = useState<string[]>(['Salary', 'Travel Allowance']);
  
  // Payout Modal State
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [activeStaff, setActiveStaff] = useState<Staff | null>(null);
  const [activeStaffExpenses, setActiveStaffExpenses] = useState<Expense[]>([]);
  const [payoutStep, setPayoutStep] = useState<1 | 2>(1);
  const [payoutCategory, setPayoutCategory] = useState<string>('Salary');
  const [payoutAmount, setPayoutAmount] = useState<string>('');
  const [isConfirming, setIsConfirming] = useState(false);

  // Manual payment recording fields (Step 2)
  const [paymentMode, setPaymentMode] = useState('UPI');
  const [paymentAccount, setPaymentAccount] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [remarks, setRemarks] = useState('');

  // Payout Accounts Management State
  const [isAccountsDialogOpen, setIsAccountsDialogOpen] = useState(false);
  const [accountsList, setAccountsList] = useState<PayoutAccount[]>([]);
  const [newAccountName, setNewAccountName] = useState('');
  const [isAddingAccount, setIsAddingAccount] = useState(false);

  // Settlements Details Overlay Dialog State
  const [isSettlementDialogOpen, setIsSettlementDialogOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
  const [settledExpenses, setSettledExpenses] = useState<any[]>([]);
  const [loadingSettlements, setLoadingSettlements] = useState(false);
  
  const { toast } = useToast();

  useEffect(() => {
    // Generate last 6 months for selection
    const months = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      months.push(`${year}-${month}`);
    }
    setAvailableMonths(months);
    setSelectedMonth(months[0]); // default to current month
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      loadData();
      loadAccounts();
      loadCategories();
      const interval = setInterval(() => loadData(true), 10000);
      return () => clearInterval(interval);
    }
  }, [selectedMonth]);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // 1. Get all staff
      const staffs = await staffService.getAll();
      setStaffList(staffs);

      // 2. Get payouts for selected month
      const monthPayouts = await payoutService.getAll({ month: selectedMonth });
      setPayouts(monthPayouts);

      // 3. Get monthly start and end dates
      const [yearStr, monthStr] = selectedMonth.split('-');
      const year = parseInt(yearStr);
      const monthIdx = parseInt(monthStr) - 1;
      const startDate = `${selectedMonth}-01`;
      const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
      const endDate = `${selectedMonth}-${String(daysInMonth).padStart(2, '0')}`;

      // 4. Load attendance stats for each staff member for this month
      const statsMap: Record<string, any> = {};
      const statsPromises = staffs.map(async (s) => {
        try {
          const stats = await attendanceService.getStats('custom', s.id, startDate, endDate);
          statsMap[s.id] = stats.staffDetail;
        } catch (e) {
          console.error(`Failed to load stats for staff ${s.id}`, e);
        }
      });
      await Promise.all(statsPromises);
      setStaffStats(statsMap);

    } catch (error) {
      console.error('Failed to load payout data', error);
      if (!silent) {
        toast({
          title: 'Error loading data',
          description: 'Failed to fetch staff payroll statistics.',
          variant: 'destructive'
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await payoutAccountService.getAll();
      setAccountsList(data);
    } catch (e) {
      console.error('Failed to load payout accounts', e);
    }
  };

  const loadCategories = async () => {
    try {
      const cats = await expenseCategoryService.getAll();
      const names = cats.map(c => c.name);
      const combined = Array.from(new Set(['Salary', 'Travel Allowance', ...names]));
      setCategoriesList(combined);
    } catch (e) {
      console.error('Failed to load categories', e);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await loadAccounts();
    await loadCategories();
    setRefreshing(false);
  };

  const openPayoutModal = async (staff: Staff) => {
    setActiveStaff(staff);
    setPayoutStep(1);
    setPayoutCategory('Salary');
    setPayoutAmount('');
    setIsConfirming(false);
    setPaymentMode('UPI');
    setPaymentAccount(accountsList[0]?.name || '');
    setBankReference('');
    setRemarks('');
    setIsPayoutModalOpen(true);

    try {
      const expenses = await expenseService.getAll({ staffId: staff.id, status: 'approved' });
      setActiveStaffExpenses(expenses);
      
      // Auto-calculate Salary payout amount
      const stats = staffStats[staff.id];
      const earnedSalary = stats ? stats.earnedSalary : 0;
      
      const staffMonthPayouts = payouts.filter(p => p.staffId === staff.id && p.category === 'Salary');
      const paidSalary = staffMonthPayouts.reduce((sum, p) => sum + p.amount, 0);
      
      const dueSalary = Math.max(0, earnedSalary - paidSalary);
      setPayoutAmount(dueSalary > 0 ? dueSalary.toString() : '0');
    } catch (e) {
      console.error('Failed to load active staff expenses', e);
    }
  };

  const handleCategoryChange = (cat: string) => {
    setPayoutCategory(cat);
    if (!activeStaff) return;
    
    const stats = staffStats[activeStaff.id];
    
    if (cat === 'Salary') {
      const earnedSalary = stats ? stats.earnedSalary : 0;
      const paidSalary = payouts
        .filter(p => p.staffId === activeStaff.id && p.category === 'Salary')
        .reduce((sum, p) => sum + p.amount, 0);
      const dueSalary = Math.max(0, earnedSalary - paidSalary);
      setPayoutAmount(dueSalary > 0 ? dueSalary.toString() : '0');
    } else if (cat === 'Travel Allowance') {
      const travelExpenses = activeStaffExpenses
        .filter(e => e.category === 'Travel Allowance' && e.date.startsWith(selectedMonth))
        .reduce((sum, e) => sum + e.amount, 0);
        
      const paidTravel = payouts
        .filter(p => p.staffId === activeStaff.id && p.category === 'Travel Allowance')
        .reduce((sum, p) => sum + p.amount, 0);
        
      const dueTravel = Math.max(0, travelExpenses - paidTravel);
      setPayoutAmount(dueTravel > 0 ? dueTravel.toString() : '0');
    } else {
      const catExpenses = activeStaffExpenses
        .filter(e => e.category === cat && e.date.startsWith(selectedMonth))
        .reduce((sum, e) => sum + e.amount, 0);
        
      const paidCat = payouts
        .filter(p => p.staffId === activeStaff.id && p.category === cat)
        .reduce((sum, p) => sum + p.amount, 0);
        
      const dueCat = Math.max(0, catExpenses - paidCat);
      setPayoutAmount(dueCat > 0 ? dueCat.toString() : '0');
    }
  };

  const advanceToConfirm = () => {
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Payout amount must be greater than 0.',
        variant: 'destructive'
      });
      return;
    }
    setPayoutStep(2);
  };

  const confirmPayout = async () => {
    if (!activeStaff) return;
    const amount = parseFloat(payoutAmount);
    
    if (isNaN(amount) || amount <= 0) {
      toast({
        title: 'Invalid Amount',
        description: 'Payout amount must be greater than 0.',
        variant: 'destructive'
      });
      return;
    }

    setIsConfirming(true);
    try {
      await payoutService.create({
        staffId: activeStaff.id,
        staffName: activeStaff.name,
        upiId: activeStaff.upiId || undefined,
        amount: amount,
        category: payoutCategory,
        month: selectedMonth,
        paymentMode,
        paymentAccount,
        remarks: remarks.trim() || undefined,
        bankReference: bankReference.trim() || undefined
      });

      toast({
        title: 'Payout Recorded',
        description: `Successfully logged ₹${amount.toLocaleString('en-IN')} ${payoutCategory} payout for ${activeStaff.name}.`,
      });
      
      setIsPayoutModalOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to record payout', error);
      toast({
        title: 'Payout Failed',
        description: 'Failed to save payout transaction to database.',
        variant: 'destructive'
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDeletePayout = async (id: string) => {
    if (!confirm('Are you sure you want to delete this payout transaction? This will restore the paid amount back to the employee\'s wallet balance.')) {
      return;
    }
    
    try {
      await payoutService.delete(id);
      toast({
        title: 'Payout Deleted',
        description: 'Successfully deleted payout transaction and reverted staff wallet balance.',
      });
      loadData();
    } catch (error) {
      console.error('Failed to delete payout', error);
      toast({
        title: 'Delete Failed',
        description: 'Failed to delete payout transaction from database.',
        variant: 'destructive'
      });
    }
  };

  const handleAddAccount = async () => {
    if (!newAccountName.trim()) return;
    setIsAddingAccount(true);
    try {
      await payoutAccountService.create(newAccountName.trim());
      setNewAccountName('');
      await loadAccounts();
      toast({ title: 'Payout account added' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Failed to add account', variant: 'destructive' });
    } finally {
      setIsAddingAccount(false);
    }
  };

  const handleDeleteAccount = async (id: string) => {
    if (!confirm('Are you sure you want to delete this payout account?')) return;
    try {
      await payoutAccountService.delete(id);
      await loadAccounts();
      toast({ title: 'Payout account deleted' });
    } catch (e) {
      console.error(e);
      toast({ title: 'Failed to delete account', variant: 'destructive' });
    }
  };

  const viewSettlements = async (payout: Payout) => {
    setSelectedPayout(payout);
    setLoadingSettlements(true);
    setSettledExpenses([]);
    setIsSettlementDialogOpen(true);
    try {
      const data = await payoutService.getSettlements(payout.id);
      setSettledExpenses(data);
    } catch (e) {
      console.error('Failed to fetch settlements', e);
      toast({
        title: 'Error loading details',
        description: 'Could not fetch settled expenses for this payout.',
        variant: 'destructive'
      });
    } finally {
      setLoadingSettlements(false);
    }
  };

  const generateUpiLink = (upiId: string, name: string, amount: number) => {
    const pName = encodeURIComponent(name);
    const pRemark = encodeURIComponent(`${payoutCategory} Payout - Royal 300`);
    return `upi://pay?pa=${upiId}&pn=${pName}&am=${amount.toFixed(2)}&tn=${pRemark}&cu=INR`;
  };

  const filteredStaffList = staffList.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.department.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredPayouts = payouts.filter(p => 
    p.staffName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.category || 'Salary').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.paymentMode || 'UPI').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.paymentAccount || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Staff Payroll & Payouts</h2>
          <p className="text-sm text-slate-500 font-medium">Record payroll payments, manage payment accounts, and inspect paid transaction details.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            onClick={() => setIsAccountsDialogOpen(true)}
            className="h-11 rounded-xl border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800 gap-2 font-semibold"
          >
            <CreditCard className="w-4 h-4" />
            Payout Accounts
          </Button>

          <div className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-800 px-3 bg-white dark:bg-slate-900/40 h-11 shrink-0">
            <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="border-none bg-transparent focus-visible:ring-0 p-0 text-sm font-bold shadow-none h-auto w-[120px]">
                <SelectValue placeholder="Select Month" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {availableMonths.map(m => {
                  const [y, mm] = m.split('-');
                  const dateName = new Date(parseInt(y), parseInt(mm) - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
                  return <SelectItem key={m} value={m}>{dateName}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            className="h-11 w-11 rounded-xl border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <RefreshCw className={`w-4 h-4 text-slate-500 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Tabs Switcher */}
      <div className="flex border-b border-slate-200 dark:border-slate-800">
        <button
          onClick={() => { setActiveTab('payroll'); setSearchQuery(''); }}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 transition-all ${
            activeTab === 'payroll'
              ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
              : 'border-transparent text-slate-450 hover:text-slate-705 dark:hover:text-slate-200'
          }`}
        >
          <DollarSign className="w-4.5 h-4.5" />
          Active Payroll
        </button>
        <button
          onClick={() => { setActiveTab('paid-transactions'); setSearchQuery(''); }}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 transition-all ${
            activeTab === 'paid-transactions'
              ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
              : 'border-transparent text-slate-450 hover:text-slate-705 dark:hover:text-slate-200'
          }`}
        >
          <History className="w-4.5 h-4.5" />
          PAID Transactions
        </button>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-slate-400" />
        <Input
          placeholder={activeTab === 'payroll' ? "Search staff by name, email, or department..." : "Search paid transactions by staff, category, mode, account..."}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-11.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/40 w-full"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="flex flex-col items-center gap-3">
            <span className="w-9 h-9 border-3 border-indigo-650 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm font-semibold text-slate-400">Loading details...</p>
          </div>
        </div>
      ) : activeTab === 'payroll' ? (
        /* Payroll Cards Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredStaffList.map(staff => {
            const stats = staffStats[staff.id];
            const staffPayouts = payouts.filter(p => p.staffId === staff.id);
            const totalPaid = staffPayouts.reduce((sum, p) => sum + p.amount, 0);
            
            return (
              <GlassCard key={staff.id} className="border-slate-200/50 dark:border-slate-800 shadow-sm rounded-2xl bg-white/70 dark:bg-slate-900/60 backdrop-blur-xl flex flex-col justify-between overflow-hidden">
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-500" />
                <CardContent className="p-5 space-y-4 flex-1 flex flex-col justify-between">
                  <div>
                    {/* Staff Header */}
                    <div className="flex items-center gap-3 border-b border-slate-100 dark:border-slate-800/60 pb-3">
                      <div className="w-11 h-11 rounded-xl bg-indigo-500/10 dark:bg-indigo-500/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0 font-extrabold text-base">
                        {staff.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-sm text-slate-800 dark:text-slate-200 truncate">{staff.name}</h3>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold truncate uppercase tracking-wider">{staff.department} • {staff.position}</p>
                      </div>
                    </div>

                    {/* Pay stats */}
                    <div className="grid grid-cols-2 gap-3 pt-3 text-xs">
                      <div className="bg-slate-50 dark:bg-slate-950/40 p-2.5 rounded-xl border border-slate-100 dark:border-slate-800/60">
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider">Unpaid Wallet</p>
                        <p className="text-base font-black text-slate-800 dark:text-slate-200 mt-0.5">₹{(staff.walletBalance || 0).toLocaleString('en-IN')}</p>
                      </div>
                      <div className="bg-slate-50 dark:bg-slate-900/30 p-2.5 rounded-xl border border-slate-100 dark:border-slate-800/60">
                        <p className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider">Earned Salary</p>
                        <p className="text-base font-black text-emerald-600 dark:text-emerald-400 mt-0.5">₹{stats ? stats.earnedSalary.toLocaleString('en-IN') : '0'}</p>
                      </div>
                    </div>

                    {/* Attendances & Leaves Details */}
                    <div className="mt-3.5 space-y-2 border-t border-slate-100 dark:border-slate-800/60 pt-3">
                      <div className="flex justify-between text-xs font-semibold text-slate-550">
                        <span>Wages Rate:</span>
                        <span className="text-slate-700 dark:text-slate-300 font-bold">₹{staff.totalWages?.toLocaleString('en-IN')}/mo</span>
                      </div>
                      <div className="flex justify-between text-xs font-semibold text-slate-555">
                        <span>Paid Days:</span>
                        <span className="text-slate-700 dark:text-slate-300 font-bold">{stats ? stats.daysPaid : '0'} days</span>
                      </div>
                      <div className="flex justify-between text-xs font-semibold text-slate-555">
                        <span>UPI ID:</span>
                        {staff.upiId ? (
                          <span className="text-emerald-600 dark:text-emerald-400 font-bold truncate max-w-[150px]">{staff.upiId}</span>
                        ) : (
                          <span className="text-rose-500 font-bold flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Config Required</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer Actions */}
                  <div className="mt-5 pt-3 border-t border-slate-100 dark:border-slate-800/60 space-y-3">
                    {totalPaid > 0 && (
                      <div className="bg-emerald-500/5 dark:bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-2.5 flex items-center gap-2.5">
                        <CheckCircle className="w-4.5 h-4.5 text-emerald-500 shrink-0" />
                        <div className="text-xs">
                          <p className="font-extrabold text-emerald-600 dark:text-emerald-400">Paid So Far: ₹{totalPaid.toLocaleString('en-IN')}</p>
                          <p className="text-[9px] text-slate-400 dark:text-slate-500 font-medium">({staffPayouts.length} transaction{staffPayouts.length > 1 ? 's' : ''})</p>
                        </div>
                      </div>
                    )}
                    
                    <Button
                      onClick={() => openPayoutModal(staff)}
                      className="w-full h-10.5 rounded-xl font-bold bg-indigo-650 hover:bg-indigo-500 text-white shadow-md shadow-indigo-600/10 flex items-center justify-center gap-2"
                    >
                      <Banknote className="w-4 h-4" />
                      Process Payout
                    </Button>
                  </div>
                </CardContent>
              </GlassCard>
            );
          })}
        </div>
      ) : (
        /* Historical PAID Transactions Table */
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-150 flex items-center gap-2">
                <Banknote className="w-5 h-5 text-indigo-500" />
                Recent Payouts History
              </h3>
              <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">Detailed log of all processed payments. Click any row to view settled expense details.</p>
            </div>
          </div>
          
          <GlassCard className="border-slate-200/50 dark:border-slate-800/40 rounded-2xl bg-white/50 dark:bg-slate-900/40 overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50/50 dark:bg-slate-900/30">
                <TableRow className="border-slate-200/50 dark:border-slate-800/60">
                  <TableHead className="font-extrabold text-[10px] uppercase tracking-wider">Date & Time</TableHead>
                  <TableHead className="font-extrabold text-[10px] uppercase tracking-wider">Staff Name</TableHead>
                  <TableHead className="font-extrabold text-[10px] uppercase tracking-wider">Category</TableHead>
                  <TableHead className="font-extrabold text-[10px] uppercase tracking-wider">Method</TableHead>
                  <TableHead className="font-extrabold text-[10px] uppercase tracking-wider">Ref & Remark</TableHead>
                  <TableHead className="font-extrabold text-[10px] uppercase tracking-wider text-right">Amount Paid</TableHead>
                  <TableHead className="font-extrabold text-[10px] uppercase tracking-wider text-center">Status</TableHead>
                  <TableHead className="font-extrabold text-[10px] uppercase tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayouts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-12 text-center text-slate-400 font-bold">
                      No payouts logged for {selectedMonth} yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPayouts.map(p => (
                    <TableRow 
                      key={p.id} 
                      onClick={(e) => {
                        // Prevent modal trigger if clicking delete button or icon
                        if ((e.target as HTMLElement).closest('button')) return;
                        viewSettlements(p);
                      }}
                      className="border-slate-200/50 dark:border-slate-800/60 font-semibold text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100/40 dark:hover:bg-slate-800/20 cursor-pointer transition-colors"
                    >
                      <TableCell>
                        {new Date(p.createdAt).toLocaleDateString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })} @ {new Date(p.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell className="font-bold text-slate-800 dark:text-slate-100">{p.staffName}</TableCell>
                      <TableCell>
                        <Badge className="bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-bold border border-indigo-500/20 rounded-md text-[10px]">
                          {p.category || 'Salary'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        <span className="text-[11px] font-bold text-slate-800 dark:text-slate-200">
                          {p.paymentMode || 'UPI'}
                        </span>
                        {p.paymentAccount && (
                          <span className="text-[10px] text-slate-450 ml-1">
                            ({p.paymentAccount})
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        <span className="text-[10px] text-slate-400 dark:text-slate-450 font-mono block">
                          Ref: {p.bankReference || '—'}
                        </span>
                        {p.remarks && (
                          <span className="text-[11px] text-slate-500 block truncate">
                            {p.remarks}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="font-bold text-right text-emerald-600 dark:text-emerald-400 font-mono">₹{p.amount.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-bold rounded-lg text-[10px] px-2 py-0.5 uppercase tracking-wide">
                          Paid
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                          onClick={() => handleDeletePayout(p.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </GlassCard>
        </div>
      )}

      {/* Two-Step Payout Processing Dialog */}
      <Dialog open={isPayoutModalOpen} onOpenChange={setIsPayoutModalOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0 overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-500 via-indigo-650 to-indigo-700 px-6 py-5 text-white flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              {payoutStep === 1 ? <Banknote className="w-5.5 h-5.5" /> : <QrCode className="w-5.5 h-5.5" />}
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-white">
                {payoutStep === 1 ? 'Step 1: Set Payout Details' : 'Step 2: Confirm Payment'}
              </DialogTitle>
              <DialogDescription className="text-xs text-indigo-100 font-medium">
                {payoutStep === 1 ? 'Choose the expense category and edit the payout amount.' : 'Scan QR code and enter reference number to complete.'}
              </DialogDescription>
            </div>
          </div>

          {activeStaff && (
            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              {/* Staff Info Card */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-900/30 border border-slate-100 dark:border-slate-800/60">
                <div className="w-10 h-10 rounded-full bg-indigo-500/10 flex items-center justify-center text-indigo-600 font-extrabold text-sm shrink-0">
                  {activeStaff.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-extrabold text-sm text-slate-800 dark:text-slate-200">{activeStaff.name}</h4>
                  <p className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">UPI ID: {activeStaff.upiId || 'Not Configured'}</p>
                </div>
              </div>

              {payoutStep === 1 ? (
                /* Step 1: Category Selection & Amount */
                <div className="space-y-4 animate-in fade-in duration-200">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Expense Category</Label>
                    <select
                      value={payoutCategory}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      className="w-full h-11 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-sm font-semibold text-slate-800 dark:text-slate-100"
                    >
                      {categoriesList.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">Payout Amount (₹)</Label>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={payoutAmount}
                      onChange={(e) => setPayoutAmount(e.target.value)}
                      className="h-11.5 rounded-xl border-slate-200 dark:border-slate-800 font-extrabold text-base"
                    />
                  </div>

                  {/* Informational due statistics for selected category */}
                  <div className="p-3 bg-slate-50 dark:bg-slate-955/35 border border-slate-100 dark:border-slate-800/60 rounded-xl space-y-1.5 text-xs">
                    <div className="flex justify-between font-medium text-slate-500">
                      <span>Total Unpaid Wallet:</span>
                      <span className="font-bold text-slate-800 dark:text-slate-200">₹{(activeStaff.walletBalance || 0).toLocaleString('en-IN')}</span>
                    </div>
                    {payoutCategory === 'Salary' && (
                      <div className="flex justify-between font-medium text-slate-500">
                        <span>Month Salary Earned:</span>
                        <span className="font-bold text-slate-800 dark:text-slate-200">₹{(staffStats[activeStaff.id]?.earnedSalary || 0).toLocaleString('en-IN')}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Step 2: Confirm Payment Details & UPI QR */
                <div className="space-y-4 animate-in fade-in duration-200">
                  {/* Summary Callout */}
                  <div className="p-3 bg-indigo-50/15 dark:bg-indigo-950/10 border border-indigo-100/40 rounded-xl space-y-1 text-xs">
                    <p className="font-bold text-slate-800 dark:text-slate-200">Payment Summary</p>
                    <div className="grid grid-cols-2 gap-y-1 pt-1.5 text-slate-500">
                      <span>Category:</span>
                      <span className="font-bold text-slate-800 dark:text-slate-150">{payoutCategory}</span>
                      <span>Amount:</span>
                      <span className="font-bold text-emerald-600 dark:text-emerald-400 font-mono">₹{parseFloat(payoutAmount).toLocaleString('en-IN')}</span>
                    </div>
                  </div>

                  <div className="space-y-3.5">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs font-bold text-slate-500">Payment Mode</Label>
                        <select
                          value={paymentMode}
                          onChange={(e) => setPaymentMode(e.target.value)}
                          className="w-full h-10.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-sm font-semibold"
                        >
                          <option value="UPI">UPI</option>
                          <option value="Net Banking">Net Banking</option>
                          <option value="HDFC Bank">HDFC Bank</option>
                          <option value="Jio Payment Bank">Jio Payment Bank</option>
                          <option value="Cash">Cash</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div className="space-y-1.5">
                        <Label className="text-xs font-bold text-slate-500">Paid From Account</Label>
                        <select
                          value={paymentAccount}
                          onChange={(e) => setPaymentAccount(e.target.value)}
                          className="w-full h-10.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-sm font-semibold"
                        >
                          <option value="">Choose account...</option>
                          {accountsList.map(acc => (
                            <option key={acc.id} value={acc.name}>{acc.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-bold text-slate-500">Bank Reference Number</Label>
                      <Input
                        placeholder="e.g. TXN100249214732"
                        value={bankReference}
                        onChange={(e) => setBankReference(e.target.value)}
                        className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-xs font-bold text-slate-500">Remarks / Notes</Label>
                      <Input
                        placeholder="Payout details..."
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                      />
                    </div>
                  </div>

                  {/* UPI QR Reference if applicable */}
                  {parseFloat(payoutAmount) > 0 && activeStaff.upiId && paymentMode === 'UPI' && (
                    <div className="p-3 bg-indigo-50/15 dark:bg-indigo-950/10 border border-indigo-100/40 rounded-xl space-y-3">
                      <p className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-500">Reference QR Code</p>
                      <div className="flex flex-col sm:flex-row items-center gap-3">
                        <div className="p-2 bg-white border border-slate-100 rounded-xl shrink-0">
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(
                              generateUpiLink(activeStaff.upiId, activeStaff.name, parseFloat(payoutAmount))
                            )}`}
                            alt="UPI Payment QR Code"
                            className="w-24 h-24"
                          />
                        </div>
                        <div className="text-xs space-y-1">
                          <p className="font-bold">Scan to open payment flow</p>
                          <p className="text-slate-400 dark:text-slate-500 text-[10px]">Use GPay, PhonePe, Paytm, or BHIM.</p>
                          <a
                            href={generateUpiLink(activeStaff.upiId, activeStaff.name, parseFloat(payoutAmount))}
                            className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline flex items-center gap-0.5 mt-1"
                          >
                            Deep link redirection <ArrowUpRight className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="bg-slate-50 dark:bg-slate-900/40 px-6 py-4 border-t border-slate-100 dark:border-slate-800/60 flex flex-col sm:flex-row gap-2 shrink-0">
            {payoutStep === 1 ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsPayoutModalOpen(false)}
                  className="rounded-xl border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-850 font-bold"
                >
                  Cancel
                </Button>
                <Button
                  onClick={advanceToConfirm}
                  disabled={!payoutAmount || parseFloat(payoutAmount) <= 0}
                  className="rounded-xl font-bold bg-indigo-650 hover:bg-indigo-600 text-white flex items-center justify-center gap-1.5 shadow-md shadow-indigo-600/10"
                >
                  Next
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setPayoutStep(1)}
                  className="rounded-xl border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-850 font-bold flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
                {activeStaff && (
                  <Button
                    onClick={confirmPayout}
                    disabled={isConfirming}
                    className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-1.5 shadow-md shadow-emerald-600/10"
                  >
                    {isConfirming && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    Confirm Payment
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settled Expenses Details Dialog */}
      <Dialog open={isSettlementDialogOpen} onOpenChange={setIsSettlementDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col p-0 overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl">
          <div className="bg-gradient-to-r from-indigo-500 via-indigo-650 to-indigo-700 px-6 py-5 text-white flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Info className="w-5.5 h-5.5" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-white">Settled Expenses Details</DialogTitle>
              <DialogDescription className="text-xs text-indigo-100 font-medium">
                Detailed list of all expense transactions settled by this payment.
              </DialogDescription>
            </div>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {selectedPayout && (
              <div className="grid grid-cols-2 gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-900/30 border border-slate-100 dark:border-slate-800/60 text-xs">
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Employee Name</p>
                  <p className="font-bold text-slate-800 dark:text-slate-100 mt-0.5">{selectedPayout.staffName}</p>
                </div>
                <div>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Payment Category</p>
                  <Badge className="bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-bold border border-indigo-500/20 rounded-md text-[10px] mt-0.5">
                    {selectedPayout.category || 'Salary'}
                  </Badge>
                </div>
                <div className="mt-2">
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Total Amount Paid</p>
                  <p className="font-black text-emerald-600 dark:text-emerald-400 text-sm mt-0.5 font-mono">₹{selectedPayout.amount.toLocaleString('en-IN')}</p>
                </div>
                <div className="mt-2">
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Payment Date</p>
                  <p className="font-bold text-slate-800 dark:text-slate-100 mt-0.5">
                    {new Date(selectedPayout.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
            )}

            {loadingSettlements ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <RefreshCw className="w-6 h-6 text-indigo-500 animate-spin" />
                <p className="text-xs font-semibold text-slate-400">Loading settled transactions...</p>
              </div>
            ) : settledExpenses.length === 0 ? (
              <div className="text-center py-10 text-slate-400 font-bold text-xs border border-dashed border-slate-200 dark:border-slate-800 rounded-xl">
                No matching expense items were settled by this payout.
              </div>
            ) : (
              <div className="border border-slate-100 dark:border-slate-800/60 rounded-xl overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50/50 dark:bg-slate-900/20">
                    <TableRow className="border-slate-100 dark:border-slate-800/60">
                      <TableHead className="font-extrabold text-[10px] uppercase tracking-wider">Date</TableHead>
                      <TableHead className="font-extrabold text-[10px] uppercase tracking-wider">Remark / Description</TableHead>
                      <TableHead className="font-extrabold text-[10px] uppercase tracking-wider text-right">Paid here</TableHead>
                      <TableHead className="font-extrabold text-[10px] uppercase tracking-wider text-right">Original</TableHead>
                      <TableHead className="font-extrabold text-[10px] uppercase tracking-wider text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {settledExpenses.map(item => (
                      <TableRow key={item.id} className="border-slate-100 dark:border-slate-800/60 text-xs font-semibold text-slate-700 dark:text-slate-350">
                        <TableCell>
                          {new Date(item.date + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                        </TableCell>
                        <TableCell className="max-w-[150px] truncate">{item.remark || 'Daily Wage'}</TableCell>
                        <TableCell className="font-bold text-right text-emerald-600 dark:text-emerald-450 font-mono">₹{item.settledAmount.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="font-medium text-right text-slate-400 dark:text-slate-500 font-mono">₹{item.originalAmount.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-center">
                          {item.paidAmount >= item.originalAmount ? (
                            <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-bold rounded-lg text-[9px] px-1.5 py-0.2 uppercase">
                              Fully Paid
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 font-bold rounded-lg text-[9px] px-1.5 py-0.2 uppercase">
                              Partially Paid
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter className="bg-slate-50 dark:bg-slate-900/40 px-6 py-4 border-t border-slate-100 dark:border-slate-800/60 shrink-0">
            <Button
              variant="outline"
              onClick={() => setIsSettlementDialogOpen(false)}
              className="rounded-xl border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-850 font-bold"
            >
              Close Details
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout Accounts Management Dialog */}
      <Dialog open={isAccountsDialogOpen} onOpenChange={setIsAccountsDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col p-0 overflow-hidden rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl">
          <div className="bg-indigo-650 px-6 py-5 text-white flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <CreditCard className="w-5.5 h-5.5" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-white">Manage Payout Accounts</DialogTitle>
              <DialogDescription className="text-xs text-indigo-100 font-medium">Add or delete accounts from which you pay employees.</DialogDescription>
            </div>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {/* Add account form */}
            <div className="flex gap-2 items-end">
              <div className="flex-1 space-y-1">
                <Label className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Account Name</Label>
                <Input
                  placeholder="e.g. Jio Payment Bank"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  className="h-10.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddAccount(); }}
                />
              </div>
              <Button
                onClick={handleAddAccount}
                disabled={isAddingAccount || !newAccountName.trim()}
                className="bg-indigo-650 hover:bg-indigo-700 text-white rounded-xl h-10.5 font-bold"
              >
                {isAddingAccount ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                Add
              </Button>
            </div>

            {/* List of accounts */}
            <div className="border border-slate-100 dark:border-slate-800/60 rounded-2xl overflow-hidden mt-2">
              <Table>
                <TableHeader className="bg-slate-50/50 dark:bg-slate-900/20">
                  <TableRow>
                    <TableHead className="font-extrabold text-[10px] uppercase tracking-wider">Account Name</TableHead>
                    <TableHead className="font-extrabold text-[10px] uppercase tracking-wider text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accountsList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} className="py-6 text-center text-slate-400 font-bold">
                        No accounts configured yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    accountsList.map(acc => (
                      <TableRow key={acc.id} className="border-slate-100 dark:border-slate-800/60 font-semibold text-xs">
                        <TableCell className="font-bold">{acc.name}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0 text-slate-400 hover:text-rose-500 transition-colors"
                            onClick={() => handleDeleteAccount(acc.id)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <DialogFooter className="bg-slate-50 dark:bg-slate-900/40 px-6 py-4 border-t border-slate-100 dark:border-slate-800/60 shrink-0">
            <Button
              variant="outline"
              onClick={() => setIsAccountsDialogOpen(false)}
              className="rounded-xl border-slate-200 dark:border-slate-800 hover:bg-slate-100 dark:hover:bg-slate-850 font-bold"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayoutsPage;
